import {FormFieldBase} from '@hconnect/common/types'
import {DateRangePicker} from '@hconnect/uikit/src/lib2'
import {
  localDateTimeRangeToTimeRangeParam,
  timeRangeParamToLocalTime
} from '@hconnect/uikit/src/lib2/components/simpleDatePicker/dateRangePickerHelpers'
import {Control, FieldValues, useController, Path} from 'react-hook-form'
import {useTranslation} from 'react-i18next'

import {getSystemTimezone, useFormInputLabelWithSuffix} from '../../hooks'

type DateRangePickerFormFieldProps<TField extends FieldValues> = Omit<FormFieldBase, 'name'> & {
  control: Control<TField>
  startName: Path<TField>
  endName: Path<TField>
  timezone?: string
}

export const DateRangePickerFormField = <TField extends FieldValues>({
  label,
  startName,
  endName,
  control,
  disabled,
  readOnly,
  optional,
  timezone = getSystemTimezone()
}: DateRangePickerFormFieldProps<TField>) => {
  const fieldDisabled = disabled || readOnly
  const startDateController = useController({name: startName, control, disabled: fieldDisabled})
  const endDateController = useController({name: endName, control, disabled: fieldDisabled})
  const inputLabel = useFormInputLabelWithSuffix({label, optional})
  const {t} = useTranslation()

  const isTimeRangeValid =
    startDateController.field.value !== undefined && endDateController.field.value !== undefined

  return (
    <DateRangePicker
      label={inputLabel}
      errorText={
        startDateController.fieldState.error?.message || endDateController.fieldState.error?.message
      }
      disabled={fieldDisabled}
      onTimeRangeParamChange={(value) => {
        const [newStartDate, newEndDate] = timeRangeParamToLocalTime({
          timeRange: value,
          selectionItems: [],
          timezone
        })
        startDateController.field.onChange(newStartDate)
        endDateController.field.onChange(newEndDate)
      }}
      timeRangeParam={localDateTimeRangeToTimeRangeParam(
        [
          startDateController.field.value ?? endDateController.field.value,
          endDateController.field.value
        ],
        timezone
      )}
      selectionItems={[]}
      singleDateSwitchLabel={t('quickSelection.pickSingleDate')}
      timezone={timezone}
      noQuickSelection
      fullWidth
      isSingleDateDefault={!isTimeRangeValid}
      isContrastMode={false}
    />
  )
}
